// resultados.jsx
import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { SWEETALERT_THEME } from "../utils/sweetalert-theme";
import NarrativeContent from "../components/Resultados/NarrativaContent";
import ResultadoContent from "../components/Resultados/ResultadoContent";
import TradeoffContent from "../components/Resultados/TradeoffContent";
import iconReportes from "../assets/icons/file-download.svg";
import Select from "../components/SelectScenarios";

import { useAuth } from "../auth/AuthContext"; // 👈 ajusta ruta
import { useNavigate } from "react-router-dom";

//
// PathwayMenu (tabs Narrativa/Resultados)
//
function PathwayMenu({
  activeTab,
  onChangeTab,
  onExport,
  reportDisabled,
  selectedPathwayId,
  onChangePathway,
  pathwayOptions,
  loadingPathways,
  loadingResults,
}) {
  const tabs = ["Pathway Summary", "Sector Specific Results", "Multiple Indicators View"];

  return (
    <div className="relative z-[1000] mb-6 rounded-2xl border border-white/45 bg-white/35 p-3 backdrop-blur-md shadow-[0_12px_30px_rgba(15,23,42,0.08)]">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
          <label className="text-[14px] text-slate-800 font-semibold">
            Pathways:
          </label>

          <Select
            value={selectedPathwayId || ""}
            onChange={onChangePathway}
            options={pathwayOptions}
            placeholder={
              loadingPathways
                ? "Loading pathways..."
                : pathwayOptions.length === 0
                  ? "No saved pathways yet"
                  : "Select pathway"
            }
            disabled={loadingPathways || pathwayOptions.length === 0}
            variant="ethiopia"
            className="w-full sm:w-[320px]"
          />

          {loadingResults && (
            <span className="text-xs text-slate-500">Loading results...</span>
          )}
        </div>

        {/* Tabs + Export en el mismo nivel */}
        <div className="flex flex-wrap items-center gap-3 lg:justify-end">
          <div className="inline-flex w-fit flex-wrap gap-2 rounded-xl bg-white/45 p-1.5">
          {tabs.map((label) => {
            const isActive = activeTab === label;
            return (
              <button
                key={label}
                type="button"
                onClick={() => onChangeTab(label)}
                className={`rounded-xl border px-4 py-2 text-sm font-semibold transition-all duration-200 ${
                  isActive
                    ? "bg-[#1E5B4F]/90 border-[#C49A1F]/70 text-white shadow-[0_10px_24px_rgba(30,91,79,0.3)]"
                    : "bg-white/70 border-white/65 text-slate-700 hover:bg-white hover:border-[#C49A1F]/45 hover:-translate-y-0.5"
                }`}
              >
                {label}
              </button>
            );
          })}
          </div>

          <button
            type="button"
            onClick={onExport}
            disabled={reportDisabled}
            className={`inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-semibold text-white shadow-[0_10px_24px_rgba(30,91,79,0.3)] transition-all duration-200 ${
              reportDisabled
                ? "border-[#C49A1F]/30 bg-[#1E5B4F]/40 cursor-not-allowed opacity-50"
                : "border-[#C49A1F]/65 bg-[#1E5B4F]/90 hover:bg-[#1E5B4F] cursor-pointer"
            }`}
          >
            Report <img src={iconReportes} alt="icon" className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}

//
// Página principal
//
export default function Resultados() {
  const { csrfToken, accessToken } = useAuth();

  const [activeTab, setActiveTabState] = useState(
    () => localStorage.getItem("resultados_activeTab") || "Pathway Summary"
  );
  const setActiveTab = (tab) => {
    setActiveTabState(tab);
    localStorage.setItem("resultados_activeTab", tab);
  };

  // 🔽 Trayectorias
  const [pathways, setPathways] = useState([]);
  const [loadingPathways, setLoadingPathways] = useState(false);
  const [selectedPathwayId, setSelectedPathwayIdState] = useState(
    () => localStorage.getItem("resultados_selectedPathwayId") || ""
  );
  const setSelectedPathwayId = (id) => {
    setSelectedPathwayIdState(id);
    localStorage.setItem("resultados_selectedPathwayId", id || "");
  };

  // 🔢 Resultados
  const [resultsData, setResultsData] = useState(null);
  const [loadingResults, setLoadingResults] = useState(false);
  const [activeSubTab, setActiveSubTabState] = useState(
    () => localStorage.getItem("resultados_activeSubTab") || "Food"
  );
  const setActiveSubTab = (tab) => {
    setActiveSubTabState(tab);
    localStorage.setItem("resultados_activeSubTab", tab);
  };

  // 🔒 Deshabilitar botón Report hasta que todos los análisis estén listos
  const [tradeoffLoading, setTradeoffLoading] = useState(true);
  const [chartsTabsLoading, setChartsTabsLoading] = useState({});
  const handleSubTabLoadingChange = (tabKey, isLoading) => {
    setChartsTabsLoading((prev) => ({ ...prev, [tabKey]: isLoading }));
  };
  const chartsLoading = Object.values(chartsTabsLoading).some(Boolean);
  const navigate = useNavigate();

  // 1️⃣ Cargar lista de trayectorias

  const user = useAuth();

  useEffect(() => {
    if (!accessToken) return;

    const controller = new AbortController();

    const fetchPathways = async () => {
      try {
        setLoadingPathways(true);

        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/api/sendtask/pathways`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              ...(accessToken
                ? { Authorization: `Bearer ${accessToken}` }
                : {}),
              ...(csrfToken ? { "y-csrf-token": csrfToken } : {}),
            },
            credentials: "include",
            signal: controller.signal,
          },
        );

        const data = await res.json().catch(() => ({}));

        console.log("\n\n Pathways data: ", data);

        if (!res.ok) {
          throw new Error(
            data?.error || `Error loading pathways: ${res.status}`,
          );
        }

        const list = data.pathways || [];
        setPathways(list);

        // ✅ Selecciona la primera SOLO si aún no hay selección
        if (list.length > 0 && !selectedPathwayId) {
          setSelectedPathwayId(String(list[0].id));
        }
      } catch (err) {
        if (err.name === "AbortError") return;
        console.error("Error fetching pathways:", err);
      } finally {
        setLoadingPathways(false);
      }
    };

    console.log("fetchPathways()");

    fetchPathways();
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [csrfToken, accessToken]);

  // 2️⃣ Cargar resultados según la trayectoria seleccionada
  useEffect(() => {
    if (!accessToken) return;

    console.log("useEffect() 2");
    console.log("selectedPathwayId: ", selectedPathwayId);

    if (!selectedPathwayId) {
      console.log("No selectedPathwayId");
      setResultsData(null);
      setTradeoffLoading(true);
      setChartsTabsLoading({});
      return;
    }

    // Resetear mientras carga el nuevo pathway
    setTradeoffLoading(true);
    setChartsTabsLoading({});

    console.log("fetchResults()");
    console.log("selectedPathwayId: ", selectedPathwayId);

    const controller = new AbortController();

    const fetchResults = async () => {
      try {
        setLoadingResults(true);

        const params = new URLSearchParams();

        console.log(` ESTO ES LO QUE HAY ADENTRO: ${params.toString()}`);

        params.append("pathwayId", selectedPathwayId);

        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/api/results/results?${params.toString()}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              ...(accessToken
                ? { Authorization: `Bearer ${accessToken}` }
                : {}),
              ...(csrfToken ? { "y-csrf-token": csrfToken } : {}),
            },
            credentials: "include",
            signal: controller.signal,
          },
        );

        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          throw new Error(
            data?.error || `Error loading results: ${res.status}`,
          );
        }

        console.log("Results DATA HERE :  ", data);

        setResultsData(data);
      } catch (err) {
        if (err.name === "AbortError") return;
        console.error("Error fetching results:", err);
        setResultsData(null);
      } finally {
        setLoadingResults(false);
      }
    };

    fetchResults();
    return () => controller.abort();
  }, [selectedPathwayId, csrfToken, accessToken]);

  // Normalizar resultsData (tu lógica original, pero más segura)
  const resultsObj = useMemo(() => {
    if (!resultsData) return null;
    return Array.isArray(resultsData) ? (resultsData[0] ?? null) : resultsData;
  }, [resultsData]);

  if (resultsObj) {
    const [pathwayName, pathwayObject] = Object.entries(resultsObj)[0] ?? [];
    if (pathwayObject) {
      pathwayObject.id = selectedPathwayId;
      pathwayObject.user_id = user?.user?.id;

      if (Array.isArray(pathwayObject.scenarios)) {
        pathwayObject.scenarios = pathwayObject.scenarios.map((s) => ({
          ...s,
          label: s.label ?? s.id,
        }));
      }

      resultsObj[pathwayName] = pathwayObject;
    }
  }

  console.log("resultsObj: ", resultsObj);

  // Exportar PDF usando IA
  const handleExport = async () => {
    if (!resultsObj) {
      Swal.fire({
        icon: "info",
        title: "Info",
        text: "No results to export.",
        ...SWEETALERT_THEME,
      });
      return;
    }

    const pw = pathways.find((p) => String(p.id) === String(selectedPathwayId));
    const now = new Date();
    const timestamp = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}-${String(now.getDate()).padStart(2,"0")}_${String(now.getHours()).padStart(2,"0")}-${String(now.getMinutes()).padStart(2,"0")}-${String(now.getSeconds()).padStart(2,"0")}`;
    const safeName = `${(pw?.name || "results").replace(/[^\0-ÿ\w\-]+/g, "_").slice(0, 60)}_${timestamp}`;

    Swal.fire({
      title: "Generating PDF report...",
      text: "Capturing charts...",
      allowOutsideClick: false,
      didOpen: () => { Swal.showLoading(); },
    });

    try {
      const userData = user?.user || {};
      const html2canvas = (await import("html2canvas")).default;

      // Mapa: [tab principal, sub-tab, id del elemento en DOM, clave del chart para el PDF]
      const CHART_CAPTURE_MAP = [
        ["Sector Specific Results", "Food",        "food-intake-chart-section",      "chart_daily_per_capita_food_consumption"],
        ["Sector Specific Results", "Food",        "food-group-chart-section",       "chart_daily_per_capita_food_consumption_by_group"],
        ["Sector Specific Results", "Trade",       "trade-balance-chart-section",    "chart_commodities_trade"],
        ["Sector Specific Results", "Trade",       "exports-chart-section",          "chart_exports_main_commodities"],
        ["Sector Specific Results", "Trade",       "imports-chart-section",          "chart_imports_main_commodities"],
        ["Sector Specific Results", "Land use",    "landuse-chart-section",          "chart_area_by_land_cover"],
        ["Sector Specific Results", "GHG",         "ghg-chart-section",              "chart_ghg_emissions_by_source"],
        ["Sector Specific Results", "Biodiversity","biodiv-share-chart-section",     "chart_land_natural_processes"],
        ["Sector Specific Results", "Biodiversity","biodiv-protected-chart-section", "chart_evolution_protected_areas"],
        ["Multiple Indicators View", null,      "tradeoff-chart-section",         "chart_tradeoffs_analysis"],
      ];

      // Agrupar por (tab, subTab) únicos para minimizar el número de switches
      const tabSubCombos = [];
      const seen = new Set();
      for (const [tab, subTab] of CHART_CAPTURE_MAP) {
        const key = `${tab}||${subTab}`;
        if (!seen.has(key)) { seen.add(key); tabSubCombos.push([tab, subTab]); }
      }

      const previousTab = activeTab;
      const previousSubTab = activeSubTab;
      const chartsForPdf = {};
      const descriptionsForPdf = {};

      for (const [tab, subTab] of tabSubCombos) {
        setActiveTab(tab);
        if (subTab) setActiveSubTab(subTab);
        // Esperar a que React renderice y Recharts pinte el SVG
        await new Promise((resolve) => setTimeout(resolve, 900));

        // Para Multiple Indicators View: el análisis viene de una llamada IA que puede tardar varios segundos.
        // Hacer polling hasta que el texto aparezca en el DOM (máx 12s).
        if (tab === "Multiple Indicators View") {
          const maxWait = 12000;
          const pollInterval = 250;
          let waited = 0;
          while (waited < maxWait) {
            await new Promise((r) => setTimeout(r, pollInterval));
            waited += pollInterval;
            const descEl = document.getElementById("desc-chart_tradeoffs_analysis");
            if (descEl && descEl.innerText.trim().length > 10) break;
          }
        }

        const sections = CHART_CAPTURE_MAP.filter(([t, s]) => t === tab && s === subTab);
        for (const [, , sectionId, chartKey] of sections) {
          // Capturar imagen del chart
          const el = document.getElementById(sectionId);
          if (el) {
            try {
              const canvas = await html2canvas(el, { scale: 1.5, useCORS: true, logging: false });
              chartsForPdf[chartKey] = canvas.toDataURL("image/png");
            } catch (e) {
              console.warn(`[PDF] No se pudo capturar ${sectionId}:`, e);
            }
          }
          // Capturar descripción mientras la tab está activa y el DOM está montado
          const descEl = document.getElementById(`desc-${chartKey}`);
          if (descEl) {
            const descKey = `description_${chartKey.replace(/^chart_/, "")}`;
            descriptionsForPdf[descKey] = descEl.innerText?.trim() || "";
          }
        }
      }

      // Restaurar tab original
      setActiveTab(previousTab);
      setActiveSubTab(previousSubTab);

      console.log("[PDF] chartsForPdf capturados:", Object.keys(chartsForPdf));
      console.log("[PDF] descriptionsForPdf capturados:", Object.keys(descriptionsForPdf));

      Swal.fire({
        title: "Generating PDF report...",
        text: "The AI assistant is writing the report...",
        allowOutsideClick: false,
        didOpen: () => { Swal.showLoading(); },
      });

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/chat/generate-pdf-report`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          ...(csrfToken ? { "y-csrf-token": csrfToken } : {}),
        },
        credentials: "include",
        body: JSON.stringify({ resultsData: resultsObj, userData, chartsForPdf, descriptionsForPdf }),
      });
      if (!response.ok) {
        let errMsg = "Error generating the report";
        try {
          const errData = await response.json();
          errMsg = errData.details || errData.error || errMsg;
        } catch (_) {}
        throw new Error(errMsg);
      }
      const blob = await response.blob();
      // Descargar el PDF directamente
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${safeName}.pdf`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }, 100);
      Swal.close();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.message || "Could not generate the PDF.",
        ...SWEETALERT_THEME,
      });
    }
  };

  const pathwayOptions = pathways.map((p) => ({
    value: String(p.id),
    label: `${p.name}`,
  }));

  // Loading inicial
  if (loadingPathways && pathways.length === 0) {
    return (
      <div className="w-full relative p-4 md:p-6 font-montserrat min-h-screen">
        Loading pathways...
      </div>
    );
  }

  return (
    <div className="w-full relative pt-4 md:pt-6 font-montserrat min-h-screen">
      <PathwayMenu
        activeTab={activeTab}
        onChangeTab={setActiveTab}
        onExport={handleExport}
        reportDisabled={!resultsObj || tradeoffLoading || chartsLoading}
        selectedPathwayId={selectedPathwayId}
        onChangePathway={setSelectedPathwayId}
        pathwayOptions={pathwayOptions}
        loadingPathways={loadingPathways}
        loadingResults={loadingResults}
      />

      {/* No results */}
      {!resultsObj && selectedPathwayId && !loadingResults && (
        <div className="text-sm text-[#7E8081]">
          No results were found for this path.
        </div>
      )}

      {/* CONTENIDO */}
      {resultsObj && activeTab === "Pathway Summary" && (
        <NarrativeContent resultsData={resultsObj} />
      )}

      {resultsObj && activeTab === "Sector Specific Results" && (
        <ResultadoContent
          resultsData={resultsObj}
          activeSubTab={activeSubTab}
          onSubTabChange={setActiveSubTab}
          onSubTabLoadingChange={handleSubTabLoadingChange}
        />
      )}

      {/* Trade-offs: siempre montado para que el análisis IA ya esté listo al exportar.
          Se oculta visualmente cuando la tab no está activa. */}
      {resultsObj && (
        <div style={{ display: activeTab === "Multiple Indicators View" ? "block" : "none" }}>
          <TradeoffContent resultsData={resultsObj} onLoadingChange={setTradeoffLoading} />
        </div>
      )}
    </div>
  );
}
